import * as React from 'react';
import { Link } from 'react-router-dom';
import { useDashboardBudgetSummaries } from '@/hooks/useBudgets';
import { useUser } from '@/hooks/useUser';
import { useAuth } from '@/contexts/AuthContext';
import { useSync } from '@/contexts/SyncContext';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { LoadingCard } from '@/components/ui/Loading';
import { DashboardBudgetCard } from '@/components/budget/DashboardBudgetCard';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { formatCurrency } from '@/lib/utils';
import type { QueuedChange } from '@/lib/db';
import type { BudgetSummary } from '@/types';
import { filterPastRootBudgets, filterRootBudgets } from './dashboardBudgetFilters';
import { Page } from '@/components/layout/Page';
import { getGreetingName } from '@/lib/userDisplay';

export function DashboardPage() {
  const { user: authUser } = useAuth();
  const { data: userData } = useUser();
  const { isOnline, pendingChanges, lastSyncTime, getPendingChanges } = useSync();
  const { data: allBudgets = [], isLoading: budgetsLoading } = useDashboardBudgetSummaries();
  const [showPendingChanges, setShowPendingChanges] = React.useState(false);
  const [changesData, setChangesData] = React.useState<QueuedChange[]>([]);
  const [loadingChanges, setLoadingChanges] = React.useState(false);

  const today = React.useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const isCurrent = React.useCallback(
    (b: { startDate: string; endDate: string }) => {
      const start = new Date(b.startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(b.endDate);
      end.setHours(0, 0, 0, 0);
      return today >= start && today <= end;
    },
    [today]
  );

  // Only show root budgets (budgets without a parent), current first
  const orderedBudgets = React.useMemo(() => {
    const rootBudgets = filterRootBudgets(allBudgets).filter(isCurrent);
    rootBudgets.sort((a, b) => {
      const aCurrent = isCurrent(a);
      const bCurrent = isCurrent(b);
      if (aCurrent !== bCurrent) return aCurrent ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    return rootBudgets;
  }, [allBudgets, isCurrent]);

  const pastBudgets = React.useMemo(() => {
    const activeRootIds = new Set(orderedBudgets.map(b => b.id));
    return filterPastRootBudgets(allBudgets, activeRootIds);
  }, [allBudgets, orderedBudgets]);

  const overBudgeted = React.useMemo(() => {
    return filterRootBudgets(allBudgets)
      .filter(isCurrent)
      .filter((budget) => {
        const spent = budget.spent ?? 0;
        const remaining = budget.remaining ?? (budget.amount - spent);
        return remaining < 0;
      })
      .map((budget) => {
        const spent = budget.spent ?? 0;
        const remaining = budget.remaining ?? (budget.amount - spent);
        return {
          ...budget,
          overAmount: Math.abs(remaining),
        };
      });
  }, [allBudgets, isCurrent]);

  const handleShowChanges = async () => {
    setLoadingChanges(true);
    try {
      const changes = await getPendingChanges();
      setChangesData(changes);
      setShowPendingChanges(true);
    } finally {
      setLoadingChanges(false);
    }
  };

  const greetingName = getGreetingName(userData ?? authUser);

  return (
    <Page className="space-y-6">
      {/* Welcome header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Hello{greetingName ? `, ${greetingName}` : ''}
          </h1>
          <p className="text-sm text-muted-foreground">
            Here's your financial overview
          </p>
        </div>
        <Link to="/budgets/new">
          <Button size="sm">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mr-1"
            >
              <path d="M5 12h14" />
              <path d="M12 5v14" />
            </svg>
            New Budget
          </Button>
        </Link>
      </div>

      {/* Alerts */}
      {overBudgeted.length > 0 && (
        <Card className="border-warning/50 bg-warning/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-warning/10 p-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-warning"
                >
                  <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                  <path d="M12 9v4" />
                  <path d="M12 17h.01" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-foreground">
                  {overBudgeted.length} budget{overBudgeted.length !== 1 ? 's' : ''} over limit
                </p>
                <p className="text-sm text-muted-foreground">
                  Review your spending to get back on track
                </p>
              </div>
            </div>
            <div className="mt-3 space-y-1">
              {overBudgeted.map((budget) => (
                <div key={budget.id} className="flex items-center justify-between text-sm">
                  <Link to={`/budgets/${budget.id}`} className="text-foreground hover:underline">
                    {budget.name}
                  </Link>
                  <span className="text-warning font-medium">
                    {formatCurrency(budget.overAmount, budget.currency)} over
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active budgets */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-foreground">Active Budgets</h2>
          <Link to="/budgets" className="text-sm text-primary hover:underline">
            View all
          </Link>
        </div>
        
        {budgetsLoading ? (
          <LoadingCard />
        ) : orderedBudgets.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-muted-foreground">No active budgets</p>
              <Link to="/budgets/new">
                <Button variant="outline" size="sm" className="mt-3">
                  Create your first budget
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {orderedBudgets.slice(0, 3).map((budget) => (
              <DashboardBudgetCard key={budget.id} budget={budget} isCurrent={isCurrent(budget)} />
            ))}
          </div>
        )}
      </section>

      {/* Past budgets */}
      <section>
        <h2 className="text-lg font-semibold text-foreground mb-3">
          Past
        </h2>
        {pastBudgets.length > 0 ? (
          <div className="grid gap-3">
            {pastBudgets.map((budget: BudgetSummary) => (
              <DashboardBudgetCard key={budget.id} budget={budget} isCurrent={isCurrent(budget)} />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-4 text-muted-foreground text-sm">
              No past budgets.
            </CardContent>
          </Card>
        )}
      </section>

      {/* Sync status — connection badge only when offline */}
      <Card data-testid="dashboard-sync-status">
        <CardContent className="p-4">
          <div className="flex items-center justify-between text-sm gap-3">
            <div className="flex items-center gap-2 min-w-0">
              {!isOnline ? (
                <>
                  <div className="h-2 w-2 shrink-0 rounded-full bg-warning" aria-hidden />
                  <span className="text-warning font-medium">Offline</span>
                </>
              ) : (
                <span className="text-muted-foreground truncate">
                  {lastSyncTime
                    ? `Last synced ${new Date(lastSyncTime).toLocaleTimeString()}`
                    : 'Ready to sync'}
                </span>
              )}
            </div>
            {(pendingChanges > 0 || !isOnline) && (
              <button
                onClick={pendingChanges > 0 ? handleShowChanges : undefined}
                className={`shrink-0 text-muted-foreground ${
                  pendingChanges > 0 ? 'hover:text-foreground cursor-pointer' : ''
                }`}
                disabled={loadingChanges || pendingChanges === 0}
              >
                {pendingChanges > 0
                  ? `${pendingChanges} pending change${pendingChanges !== 1 ? 's' : ''}`
                  : 'Will sync when online'}
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pending changes dialog */}
      <Dialog open={showPendingChanges} onOpenChange={setShowPendingChanges}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pending Changes ({changesData.length})</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {changesData.length === 0 ? (
              <p className="text-sm text-muted-foreground">No pending changes</p>
            ) : (
              changesData.map((change, idx) => (
                <Card key={idx}>
                  <CardContent className="p-3">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-sm">
                          {change.entityType} {change.changeType}
                        </div>
                        <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                          {change.changeType}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        ID: {change.entityId}
                      </div>
                      {change.data && (
                        <div className="bg-muted p-2 rounded text-xs font-mono max-h-[150px] overflow-y-auto">
                          {JSON.stringify(JSON.parse(change.data), null, 2)}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

    </Page>
  );
}
